import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, take, tap } from 'rxjs';

import { environment } from '@app/env-service';
import { Pollster } from 'v2/shared/pollster/pollster';
import { EPollsterPollMode } from 'v2/shared/pollster/pollster.const';
import { IPollsterPollParams } from 'v2/shared/pollster/pollster.types';

import { JobStatusConstant } from '../constants/job-status.constant';
import { IJobProgress } from '../models/job-progress.model';
import { IJobStatusUi } from '../models/job-status-ui.model';
import { IJobStatus } from '../models/job-status.model';

@Injectable({
  providedIn: 'root',
})
export class JobStatusService {
  url :string = environment.url;

  private readonly _jobs = new BehaviorSubject<IJobStatusUi[]>([]);
  public jobs$ = this._jobs.asObservable();

  private readonly _progress = new BehaviorSubject<IJobProgress>({
    totalJobs: 0,
    overallProgress: 0,
  });
  public progress$ = this._progress.asObservable();

  constructor(
    private http :HttpClient,
    private toastr :ToastrService,
  ) {}

  getJobCount(key ?:string) :number {
    const progress :IJobProgress = this._progress.getValue();
    if (key && Object.keys(progress).indexOf(key) >= 0) {
      return progress[key];
    }

    return progress.totalJobs;
  }

  getJobs() :void {
    this.http.get(`${this.url}/jobs`).subscribe((jobs :IJobStatus[]) => {
      const jobsData :IJobStatusUi[] = jobs.map((job :IJobStatus) => {
        return {
          id: job.id,
          jobId: job.job_id,
          type: job.type,
          queue: job.queue,
          attempts: job.attempts,
          progressNow: job.progress_now,
          progressMax: job.progress_max,
          status: job.status,
          input: job.input,
          output: job.output,
          createdAt: job.created_at,
          updatedAt: job.updated_at,
          startAt: job.start_at,
          finishedAt: job.finished_at,
          progress: this.getProgressPercentage(job.progress_now, job.progress_max),
          label: job.input?.title ? job.input?.title : job.type,
        };
      });
      this.notifyFinishedJob(jobsData);
      this.updateProgressSummary(jobsData);
      this._jobs.next(jobsData);
    });
  }
  getJobsObservable() :Observable<IJobStatus[]> {
    return this.http.get(`${this.url}/jobs`).pipe(
      tap((jobs :IJobStatus[]) => {
        const jobsData :IJobStatusUi[] = jobs.map((job :IJobStatus) => {
          return {
            id: job.id,
            jobId: job.job_id,
            type: job.type,
            queue: job.queue,
            attempts: job.attempts,
            progressNow: job.progress_now,
            progressMax: job.progress_max,
            status: job.status,
            input: job.input,
            output: job.output,
            createdAt: job.created_at,
            updatedAt: job.updated_at,
            startAt: job.start_at,
            finishedAt: job.finished_at,
            progress: this.getProgressPercentage(
              job.progress_now,
              job.progress_max,
            ),
            label: job.input?.title ? job.input?.title : job.type,
          };
        });
        this.notifyFinishedJob(jobsData);
        this.updateProgressSummary(jobsData);
        this._jobs.next(jobsData);
      }),
    );
  }

  private updateProgressSummary(currentJobs ?:IJobStatusUi[]) {
    if (!currentJobs) {
      currentJobs = this._jobs.getValue();
    }
    const activeJobs :number = currentJobs.filter(
      (job :IJobStatusUi) =>
        job.status !== JobStatusConstant.STATUS_FINISHED &&
        job.status !== JobStatusConstant.STATUS_FAILED,
    ).length;
    const progress :IJobProgress = {
      overallProgress: this.getProgressPercentage(activeJobs, currentJobs.length),
      activeJobs: activeJobs,
      totalJobs: currentJobs.length,
    };
    this._progress.next(progress);
  }

  private notifyFinishedJob(updatedJobs :IJobStatusUi[]) :void {
    const currentJobs :IJobStatusUi[] = this._jobs.getValue();
    if (currentJobs.length == 0) {
      return;
    }
    updatedJobs.forEach((updatedJob :IJobStatusUi) => {
      const job :IJobStatusUi = currentJobs.find(
        (currentJob :IJobStatusUi) => currentJob.id === updatedJob.id,
      );
      if (job && job.id) {
        if (job.status !== updatedJob.status) {
          if (updatedJob.status === JobStatusConstant.STATUS_FINISHED) {
            this.toastr.success(job.label, 'Job processing success.');
          } else if (updatedJob.status === JobStatusConstant.STATUS_FAILED) {
            this.toastr.error(job.label, 'Job processing failed.');
          }
        }
      }
    });
  }

  private getProgressPercentage(active :number, total :number) :number {
    const progress :number = total !== 0 ? Math.round((100 * active) / total) : 0;

    return progress > 100 ? 100 : progress;
  }

  createJobStatusPollster() :Pollster {
    return new Pollster({
      onStart: () => this.getJobsObservable().pipe(take(1)).subscribe(),
      onPoll: ({ mode } :IPollsterPollParams) => {
        if (mode === EPollsterPollMode.FAST) {
          this.getJobsObservable().pipe(take(1)).subscribe();
        }
      },
      shouldPollFast: () => this.getJobCount('activeJobs') > 0,
    });
  }
}
